// 189 §B (167 R7) — THE FIRM'S OWN FIELDS ON A FORM.
//
// Mounted by the client, case and collective-case modals after their built-in
// fields; renders the `custom_field` definitions that name this entity, in the
// taxonomy's order, each as the input its type asks for, bound to the row's
// `customFields` Json under the definition's code. The definitions are the
// firm's DATA (the label is the taxonomy row's, in the reader's language), so
// nothing here is a translation key except the three fixed words a yes/no or
// an empty list needs. A value the type refuses never reaches the object: the
// coercion is src/lib/crmDefinitions.mjs's, the same one the routes apply.
//
// SESSION_RULES 3b: a NEW file under src/components the brief did not name —
// three forms mount the same block, and three copies of it would be the
// duplication the tree spends slices removing. Listed first in the report.
//
// Zero-warning by construction (186's method): callbacks typed by React's
// `Dispatch`, `import React` for the JSX rule, `t` through the imported `TFn`,
// no `any`.
import React from "react";
import { NO_AUTOFILL, noAutofillClass } from "../lib/noAutofill"; // 213B §18 — no sign-ins, no contacts, in a modal's field
import type { Dispatch } from "react";
import FieldSelect from "./FieldSelect";
import { useTaxonomy } from "../hooks/useTaxonomy";
import { useTranslation } from "../i18n";
import type { TFn } from "../lib/taxonomyTypes";
import {
  coerceCustomFieldValue,
  customFieldDefsFor,
  customFieldLabel,
  normalizeCustomFieldMeta,
} from "../lib/crmDefinitions.mjs";
import AmountField from "./AmountField"; // 208 §10 — the one amount field

type Values = Readonly<Record<string, unknown>>;
type Props = Readonly<{
  entity: "client" | "case" | "cc";
  values: Values | null | undefined;
  onChange: Dispatch<Record<string, unknown>>;
  /* Read-only when the surrounding form is (the case views' lock). */
  disabled?: boolean;
}>;

const asText = (v: unknown): string => (typeof v === "string" || typeof v === "number" ? String(v) : "");
/* The <input type> a field type asks for — a function, not a word table (the
   prose-map detector reads a module-level object of words as prose). */
function inputTypeFor(type: string): string {
  if (type === "number") return "number";
  if (type === "date") return "date";
  return "text";
}

export default function CustomFieldsBlock({ entity, values, onChange, disabled }: Props) {
  const { t, lang } = useTranslation() as { t: TFn; lang: string };
  const { entries } = useTaxonomy("custom_field") as { entries: unknown };
  const defs = customFieldDefsFor(entries, entity);
  if (!defs.length) return null;
  const current: Values = values && typeof values === "object" ? values : {};
  const set = (code: string, raw: unknown, def: (typeof defs)[number]) => {
    const r = coerceCustomFieldValue(def, raw);
    const next: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(current)) if (k !== code) next[k] = val;
    if (r.ok && r.value !== null) next[code] = r.value;
    onChange(next);
  };
  return (
    <>
      {defs.map((def) => {
        const code = String(def.code);
        const meta = normalizeCustomFieldMeta(def.meta);
        const label = customFieldLabel(def, lang);
        const v = current[code];
        if (meta.type === "bool") {
          return (
            <div className="field" key={code}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: disabled ? "default" : "pointer" }}>
                <input
                  type="checkbox"
                  checked={v === true}
                  disabled={disabled}
                  onChange={(e) => { set(code, e.target.checked ? true : "", def); }}
                />
                {label}
              </label>
            </div>
          );
        }
        if (meta.type === "select") {
          return (
            <FieldSelect
              key={code}
              label={label}
              value={asText(v)}
              onChange={(nv) => { set(code, nv, def); }}
              options={[["", `— ${t("common.none", { fallback: "None" })} —`], ...meta.options.map((o) => [o.code, o.label] as const)]}
            />
          );
        }
        const inputType = inputTypeFor(meta.type);
        return (
          <div className="field" key={code}>
            <label>{label}</label>
            {meta.type === "number" ? (
              /* 208 §10 — a firm's number field takes the one amount field: typed with a decimal comma */
              <AmountField
                value={v}
                empty=""
                inputMode="decimal"
                disabled={disabled}
                onAmount={(n) => { set(code, n == null ? "" : String(n), def); }}
              />
            ) : (
            <input
              {...NO_AUTOFILL}
              className={noAutofillClass("field-input")}
              type={inputType}
              value={asText(v)}
              disabled={disabled}
              onChange={(e) => { set(code, e.target.value, def); }}
            />
            )}
          </div>
        );
      })}
    </>
  );
}
